import type { HeaderService } from '@patsnap-utils/biz';

/**
 * Deferred 接口
 */
interface IDeferred<T = any> {
    promise: Promise<T>;
    resolve: (value: T) => void;
    reject: (reason?: any) => void;
}

/**
 * 重置 HeaderService 的 deferred
 * 用于重新初始化时创建新的 promise，避免返回旧的已 resolved 的 promise
 *
 * @param headerService - HeaderService 实例
 * @description 参考 patsnap-biz-2 中 HeaderService 构造函数的实现
 */
export function resetHeaderServiceDeferred(headerService: HeaderService) {
    (headerService as any).deferred = (() => {
        const deferred: IDeferred = Object.create({});
        deferred.promise = new Promise(function(resolve, reject) {
            deferred.resolve = resolve;
            deferred.reject = reject;
        });
        return deferred;
    })();
}

/**
 * 清除账号相关的缓存
 * 因为账号接口使用了 PersistentCache 的 Fetch Adapter，完全绕过了 Axios 拦截器
 * 所以只能通过清除 CacheStorage 来强制刷新数据
 *
 * @param accountServiceUri - 账号服务 URI
 * @param storagePrefix - 缓存存储前缀，默认为 'v1.9.0'
 */
export async function clearAccountCache(
    accountServiceUri: string,
    storagePrefix: string = 'v1.9.0',
) {
    try {
        const cacheStorage = await caches.open(storagePrefix);

        // 需要清除的账号相关接口
        const accountApiPaths = [
            '/user-service/account-info/common-data',
            '/user-service/account-info/extra-data',
            '/user-service/account-info/basic-data',
            '/user-service/account-info/additional-data',
            '/user-service/tenant/tenant-select-infos',
        ];

        // 清除所有账号相关的缓存
        for (const path of accountApiPaths) {
            const url = `${accountServiceUri}${path}`;

            try {
                await cacheStorage.delete(url);
                await cacheStorage.delete(new Request(url));
                await cacheStorage.delete(new Request(url, { method: 'GET' }));
            } catch (e) {
                // 忽略单个删除失败的错误
            }
        }
    } catch (error) {
        console.warn('[clearAccountCache] Failed to clear cache:', error);
        // 即使清除缓存失败也继续执行，因为可能是浏览器不支持 CacheStorage
    }
}
